/**
 * 🧪 AdminQuizPractice.js - Quiz & Practice Management
 * * Admin portal tab for:
 * - Listing all quizzes and practices
 * - Creating new quizzes/practices
 * - Editing existing content
 * - Publishing with optional notification
 * - Viewing stats
 */

import React, { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase-config'; // ✅ FIXED PATH
import QuizEditor from './QuizEditor';
import './AdminQuizPractice.css';

// ============================================================================
// CONSTANTS
// ============================================================================

const PROJECT_OPTIONS = [
  { value: 'whale', label: '🐋 Whale Chat', color: '#0C71C3' },
  { value: 'dolphin', label: '🐬 Dolphin Chat', color: '#00B4D8' },
  { value: 'manatee', label: '🦭 Manatee Chat', color: '#10B981' },
];

const DIFFICULTY_OPTIONS = [
  { value: 'easy', label: '🟢 Easy', color: '#10B981' },
  { value: 'medium', label: '🟡 Medium', color: '#F59E0B' },
  { value: 'hard', label: '🔴 Hard', color: '#EF4444' },
];

const TYPE_OPTIONS = [
  { value: 'quiz', label: '📝 Quiz', description: 'No feedback until end' },
  { value: 'practice', label: '📖 Practice', description: 'Instant feedback' },
];

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const AdminQuizPractice = () => {
  // State
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filters
  const [filterType, setFilterType] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Editor
  const [showEditor, setShowEditor] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  
  // Confirm dialogs
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmPublish, setConfirmPublish] = useState(null);

  // =========================================================================
  // DATA LOADING
  // =========================================================================

  useEffect(() => {
    const quizzesRef = collection(db, 'quizzes');
    const q = query(quizzesRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setQuizzes(data);
        setLoading(false);
      },
      (err) => {
        console.error('Quiz load error:', err);
        setError('Failed to load quizzes');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  // =========================================================================
  // FILTERING
  // =========================================================================

  const filteredQuizzes = quizzes.filter((quiz) => {
    if (filterType !== 'all' && quiz.type !== filterType) return false;
    if (filterProject !== 'all' && quiz.project !== filterProject) return false;
    if (filterStatus !== 'all') {
      if (filterStatus === 'active' && !quiz.isActive) return false;
      if (filterStatus === 'draft' && quiz.isActive) return false;
    }
    return true;
  });

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleCreate = () => {
    setEditingQuiz(null);
    setShowEditor(true);
  };

  const handleEdit = (quiz) => {
    setEditingQuiz(quiz);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingQuiz(null);
  };

  const handlePublish = async (quiz, sendNotification = false) => {
    try {
      const quizRef = doc(db, 'quizzes', quiz.id);
      await updateDoc(quizRef, {
        isActive: true,
        status: 'active',
        publishedAt: serverTimestamp(),
        sendNotification: sendNotification,
        updatedAt: serverTimestamp(),
      });
      setConfirmPublish(null);
    } catch (err) {
      console.error('Publish error:', err);
      alert('Failed to publish: ' + err.message);
    }
  };

  const handleUnpublish = async (quiz) => {
    try {
      const quizRef = doc(db, 'quizzes', quiz.id);
      await updateDoc(quizRef, {
        isActive: false,
        status: 'draft',
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Unpublish error:', err);
      alert('Failed to unpublish: ' + err.message);
    }
  };

  const handleDelete = async (quiz) => {
    try {
      await deleteDoc(doc(db, 'quizzes', quiz.id));
      setConfirmDelete(null);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Failed to delete: ' + err.message);
    }
  };

  // =========================================================================
  // RENDER HELPERS
  // =========================================================================

  const renderFilters = () => (
    <div className="aqp-filters">
      <div className="aqp-filter-group">
        <label>Type:</label>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)}>
          <option value="all">All Types</option>
          {TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="aqp-filter-group">
        <label>Project:</label>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
          <option value="all">All Projects</option>
          {PROJECT_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="aqp-filter-group">
        <label>Status:</label>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
        </select>
      </div>
    </div>
  );

  const renderQuizCard = (quiz) => {
    const projectOpt = PROJECT_OPTIONS.find((p) => p.value === quiz.project);
    const diffOpt = DIFFICULTY_OPTIONS.find((d) => d.value === quiz.difficulty);
    const typeOpt = TYPE_OPTIONS.find((t) => t.value === quiz.type);

    return (
      <div key={quiz.id} className={`aqp-card ${quiz.isActive ? 'active' : 'draft'}`}>
        <div className="aqp-card-header">
          <div className="aqp-card-title-row">
            <span className="aqp-card-icon">{quiz.icon || '🧪'}</span>
            <div className="aqp-card-title-container">
              <h3 className="aqp-card-title">{quiz.title}</h3>
              <div className="aqp-card-badges">
                <span
                  className="aqp-badge"
                  style={{ backgroundColor: typeOpt?.value === 'quiz' ? '#6366F1' : '#10B981' }}
                >
                  {typeOpt?.label}
                </span>
                <span className="aqp-badge" style={{ backgroundColor: projectOpt?.color }}>
                  {projectOpt?.label}
                </span>
                <span className="aqp-badge" style={{ backgroundColor: diffOpt?.color }}>
                  {diffOpt?.label}
                </span>
              </div>
            </div>
          </div>
          <div className={`aqp-status ${quiz.isActive ? 'active' : 'draft'}`}>
            {quiz.isActive ? '✅ Active' : '📝 Draft'}
          </div>
        </div>

        <p className="aqp-card-description">{quiz.description}</p>

        <div className="aqp-card-stats">
          <div className="aqp-stat">
            <span className="aqp-stat-value">{quiz.taskCount || quiz.tasks?.length || 0}</span>
            <span className="aqp-stat-label">Tasks</span>
          </div>
          <div className="aqp-stat">
            <span className="aqp-stat-value">{quiz.totalAttempts || 0}</span>
            <span className="aqp-stat-label">Attempts</span>
          </div>
          <div className="aqp-stat">
            <span className="aqp-stat-value">{quiz.averageScore || 0}%</span>
            <span className="aqp-stat-label">Avg Score</span>
          </div>
          <div className="aqp-stat">
            <span className="aqp-stat-value">{quiz.perfectScores || 0}</span>
            <span className="aqp-stat-label">Perfect</span>
          </div>
        </div>

        <div className="aqp-card-actions">
          <button className="aqp-btn aqp-btn-secondary" onClick={() => handleEdit(quiz)}>
            ✏️ Edit
          </button>

          {quiz.isActive ? (
            <button
              className="aqp-btn aqp-btn-warning"
              onClick={() => handleUnpublish(quiz)}
            >
              📥 Unpublish
            </button>
          ) : (
            <button
              className="aqp-btn aqp-btn-success"
              onClick={() => setConfirmPublish(quiz)}
            >
              🚀 Publish
            </button>
          )}

          <button
            className="aqp-btn aqp-btn-danger"
            onClick={() => setConfirmDelete(quiz)}
          >
            🗑️ Delete
          </button>
        </div>

        {quiz.notificationSent && (
          <div className="aqp-card-notification">
            📣 Notification sent to {quiz.notificationCount || 0} users
          </div>
        )}
      </div>
    );
  };

  const renderPublishModal = () => {
    if (!confirmPublish) return null;

    return (
      <div className="aqp-modal-overlay">
        <div className="aqp-modal">
          <h3>🚀 Publish Quiz</h3>
          <p>Publish "{confirmPublish.title}" and make it available to users?</p>

          <div className="aqp-modal-option">
            <label>
              <input type="checkbox" id="sendNotification" />
              <span>📣 Send push notification to interested users</span>
            </label>
            <p className="aqp-modal-hint">
              Users interested in {confirmPublish.project} will be notified
            </p>
          </div>

          <div className="aqp-modal-actions">
            <button
              className="aqp-btn aqp-btn-secondary"
              onClick={() => setConfirmPublish(null)}
            >
              Cancel
            </button>
            <button
              className="aqp-btn aqp-btn-success"
              onClick={() => {
                const sendNotif = document.getElementById('sendNotification')?.checked;
                handlePublish(confirmPublish, sendNotif);
              }}
            >
              Publish
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!confirmDelete) return null;

    return (
      <div className="aqp-modal-overlay">
        <div className="aqp-modal aqp-modal-danger">
          <h3>⚠️ Delete Quiz</h3>
          <p>Are you sure you want to delete "{confirmDelete.title}"?</p>
          <p className="aqp-modal-warning">This action cannot be undone!</p>

          <div className="aqp-modal-actions">
            <button
              className="aqp-btn aqp-btn-secondary"
              onClick={() => setConfirmDelete(null)}
            >
              Cancel
            </button>
            <button
              className="aqp-btn aqp-btn-danger"
              onClick={() => handleDelete(confirmDelete)}
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // MAIN RENDER
  // =========================================================================

  if (showEditor) {
    return (
      <QuizEditor
        quiz={editingQuiz}
        onClose={handleEditorClose}
        onSave={handleEditorClose}
      />
    );
  }

  return (
    <div className="aqp-container">
      {/* Header */}
      <div className="aqp-header">
        <div className="aqp-header-left">
          <h2>🧪 Quiz & Practice Management</h2>
          <p>Create and manage quizzes and practice sessions for users</p>
        </div>
        <button className="aqp-btn aqp-btn-primary" onClick={handleCreate}>
          ➕ Create New
        </button>
      </div>

      {/* Filters */}
      {renderFilters()}

      {/* Content */}
      {loading ? (
        <div className="aqp-loading">
          <div className="aqp-spinner" />
          <p>Loading quizzes...</p>
        </div>
      ) : error ? (
        <div className="aqp-error">
          <p>❌ {error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      ) : filteredQuizzes.length === 0 ? (
        <div className="aqp-empty">
          <span className="aqp-empty-icon">📭</span>
          <h3>No Quizzes Found</h3>
          <p>
            {quizzes.length === 0
              ? 'Create your first quiz to get started!'
              : 'No quizzes match your filters.'}
          </p>
          {quizzes.length === 0 && (
            <button className="aqp-btn aqp-btn-primary" onClick={handleCreate}>
              Create Quiz
            </button>
          )}
        </div>
      ) : (
        <div className="aqp-grid">
          {filteredQuizzes.map(renderQuizCard)}
        </div>
      )}

      {/* Modals */}
      {renderPublishModal()}
      {renderDeleteModal()}
    </div>
  );
};

export default AdminQuizPractice;